/**
 * ZİYARET TAKİP — üç katmanlı saha izleme ekranı (ZIYARET_TAKIP.md §5).
 *
 * Dört sekme, üç katman:
 *   Kokpit          → Katman 1 (gün özeti)   — yönetici panosu
 *   Ziyaret Listesi → Katman 2 (ziyaret)     — giriş/çıkış/süre/sonuç
 *   Saha İzleme     → Katman 3 (hareket)     — kronolojik akış, canlı
 *   Ziyaret Raporu  → yazdırılabilir özet
 *
 * Sekmeler arası geçiş VERİ TAŞIR: Kokpit'te bir temsilci satırına tıklanınca o gün
 * oturumu seçilir ve alt katman sekmeleri o oturuma daralır. "Bir satıra tıklayınca bir
 * alt katmana inersiniz" davranışının tamamı budur.
 *
 * Bu ekran `c360` liste altyapısını KULLANMAZ ve bu bilinçlidir: c360 tek bir OData
 * koleksiyonunun listesi içindir; burada dört farklı şekilli uç (pano/liste/akış/rapor)
 * ve sekmeler arası bağlam aktarımı var. Tasarım katmanı yine paylaşılan `.sfa`'dır.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { authHeaders } from './listPageStyles';
import './visitTracking/visitTracking.css';
import {
  activityTarget, dayEndIso, dayStartIso, downloadVt, fetchVt, fmtNum, postVt, sumDayRows,
  todayIso, WORK_TYPE_LABELS,
} from './visitTracking/shared';
import { CockpitTab } from './visitTracking/CockpitTab';
import { VisitListTab } from './visitTracking/VisitListTab';
import { FieldWatchTab } from './visitTracking/FieldWatchTab';
import { LiveStrip } from './visitTracking/LiveStrip';
import { ReportTab } from './visitTracking/ReportTab';
import { VisitDrawer } from './visitTracking/VisitDrawer';
import { GpsTab } from './visitTracking/GpsTab';
import { IcDashboard, IcList, IcRefresh, IcTimeline, IcReport, IcCalc, IcUnlock, IcPin } from './visitTracking/icons';

const TABS = [
  { key: 'cockpit', label: 'Kokpit', Icon: IcDashboard },
  { key: 'visits', label: 'Ziyaret Listesi', Icon: IcList },
  { key: 'watch', label: 'Saha İzleme', Icon: IcTimeline },
  // #776 GPS — aynı hareket verisine KONUM ekseninden bakan sekme (Saha İzleme zaman eksenlidir).
  { key: 'gps', label: 'GPS', Icon: IcPin },
  { key: 'report', label: 'Ziyaret Raporu Özet', Icon: IcReport },
];

export function VisitTrackingPage() {
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePageActions();
  usePageMeta('Ziyaret Takip', 'Saha yönetimi · gün / ziyaret / hareket');

  // ── Filtreler ──────────────────────────────────────────────────────────────
  //
  // #786 KRİTER ≠ UYGULANAN KRİTER. Ekran eskiden her alan değişiminde tüm katmanları yeniden
  // çekiyordu: kullanıcı tarih/temsilci seçerken arka arkaya ağır sorgular tetikleniyor, ekran
  // yarım kriterle veri gösteriyordu. Artık aşağıdaki alanlar yalnız FORM'dur; sorgular
  // "Listele"ye basılınca dondurulan `applied` üzerinden koşar.
  const [dateFrom, setDateFrom] = useState(todayIso);
  const [dateTo, setDateTo] = useState(todayIso);
  const [vendorId, setVendorId] = useState('');
  const [workType, setWorkType] = useState('');
  const [representativeId, setRepresentativeId] = useState('');

  /** #786 "Listele" ile dondurulan kriterler. null = henüz listelenmedi (ekran boş durumda). */
  const [applied, setApplied] = useState(null);

  const [tab, setTab] = useState('cockpit');
  const [error, setError] = useState('');

  /** Kokpit'ten seçilen gün — alt katman sekmelerinin bağlamı. */
  const [selectedDay, setSelectedDay] = useState(null);

  // ── Veri ───────────────────────────────────────────────────────────────────
  const [dashboard, setDashboard] = useState(null);
  const [days, setDays] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [visits, setVisits] = useState(null);
  const [activities, setActivities] = useState([]);
  const [dayDetail, setDayDetail] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const [lookups, setLookups] = useState({ vendors: [], representatives: [] });

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [visitDetail, setVisitDetail] = useState(null);
  const [visitDetailLoading, setVisitDetailLoading] = useState(false);

  const [autoRefresh, setAutoRefresh] = useState(true);
  const refreshSec = dashboard?.thresholds?.liveRefreshSec ?? 60;

  // #786 Sorgular UYGULANAN kriterlerden okur (form değil). Listelenmemişken hiçbir yükleme koşmaz.
  const aDateFrom = applied?.dateFrom ?? '';
  const aDateTo = applied?.dateTo ?? '';
  const aVendorId = applied?.vendorId ?? '';
  const aWorkType = applied?.workType ?? '';
  const aRepresentativeId = applied?.representativeId ?? '';

  const range = useMemo(() => ({
    dateFrom: dayStartIso(aDateFrom),
    dateTo: dayEndIso(aDateTo),
  }), [aDateFrom, aDateTo]);

  /**
   * #786 Seçili distribütöre bağlı temsilciler. Distribütörü olmayan (VendorId boş) tanım
   * listelenmez: kapsamı belirsiz bir temsilciyi distribütör filtresinin altına koymak, kullanıcıya
   * "bu distribütörün temsilcisi" demek olurdu.
   */
  const scopedRepresentatives = useMemo(() => {
    if (!vendorId) return [];
    return lookups.representatives.filter(
      (r) => String(r.VendorId ?? r.vendorId ?? '') === String(vendorId),
    );
  }, [lookups.representatives, vendorId]);

  // ── Lookup'lar (bir kez) ───────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [vRes, rRes] = await Promise.all([
          fetch('/api/Vendors?$select=Id,Name&$orderby=Name&$top=500', { headers: authHeaders() }),
          // #786 VendorId de çekilir: temsilci listesi seçili distribütöre göre daraltılacak.
          // Eskiden bu alan projeksiyonda yoktu ve ekran TÜM temsilcileri listeliyordu.
          fetch('/api/Representatives?$select=Id,Name,TextId,VendorId&$orderby=Name&$top=1000', { headers: authHeaders() }),
        ]);
        const vJson = await vRes.json().catch(() => null);
        const rJson = await rRes.json().catch(() => null);
        if (cancelled) return;
        setLookups({
          vendors: vJson?.value || vJson?.Data || [],
          representatives: rJson?.value || rJson?.Data || [],
        });
      } catch {
        // Lookup'lar dolmazsa ekran çalışmaya devam eder — filtreler "Tümü"de kalır.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Kokpit verisi ──────────────────────────────────────────────────────────
  const loadCockpit = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { ...range, vendorId: aVendorId, workType: aWorkType, userIds: aRepresentativeId };
      const [dash, dayList, alertList] = await Promise.all([
        fetchVt('/dashboard', params),
        fetchVt('/days', { ...params, groupByDay: true, take: 500 }),
        fetchVt('/alerts', { ...range, vendorId: aVendorId }),
      ]);
      setDashboard(dash);
      setDays(dayList);
      setAlerts(alertList || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [range, aVendorId, aWorkType, aRepresentativeId]);

  // ── Katman 2/3 verisi (seçili güne veya filtreye göre) ─────────────────────
  const loadLowerLayers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const sessionId = selectedDay?.id || '';
      const repId = selectedDay?.representativeId || aRepresentativeId || '';

      const visitParams = sessionId
        ? { sessionId, take: 1000 }
        : { ...range, representativeId: repId, take: 1000 };

      const activityParams = sessionId
        ? { sessionId, includeGps: false, take: 2000 }
        : { ...range, representativeId: repId, includeGps: false, take: 2000 };

      // #777.6 Bir oturum seçilmemişse sağ sütun BOŞ KALMAZ: filtredeki oturumların özeti toplanır.
      // Eskiden burada `Promise.resolve(null)` vardı ve Saha İzleme'ye Kokpit'ten geçmeyen kullanıcı
      // sağdaki kartların hiçbirinde veri göremiyordu.
      const [visitList, activityList, detail] = await Promise.all([
        fetchVt('/visits', visitParams),
        fetchVt('/activities', activityParams),
        sessionId
          ? fetchVt(`/days/${sessionId}`)
          : fetchVt('/days', {
            ...range, vendorId: aVendorId, workType: aWorkType, userIds: repId, groupByDay: true, take: 500,
          }).then((d) => sumDayRows(d?.items)),
      ]);
      setVisits(visitList);
      setActivities(activityList?.items || []);
      setDayDetail(detail);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [range, aRepresentativeId, selectedDay, aVendorId, aWorkType]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchVt('/report/summary', {
        ...range,
        vendorId: aVendorId,
        userIds: selectedDay?.representativeId || aRepresentativeId,
      });
      setReport(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [range, aVendorId, aRepresentativeId, selectedDay]);

  /**
   * #804 GPS sekmesi için gün oturumu listesi.
   *
   * GPS haritası artık Kokpit'te satır seçilmemişse filtredeki en yeni günü kendisi çizer; bunun
   * için gün listesine ihtiyacı var. Kokpit'e hiç girilmeden (Listele → doğrudan GPS) `days` boş
   * kalıyordu ve harita çizemiyordu. Burada Kokpit'in TAMAMI değil (dashboard/alerts gereksiz),
   * yalnız /days çekilir.
   *
   * ⚠ "Zaten yüklü mü" koruması `days` state'ini BAĞIMLILIK olarak DEĞİL ref üzerinden okur.
   * Bağımlılık olduğunda Kokpit sekmesi SONSUZ DÖNGÜYE giriyordu:
   *   loadCockpit → setDays(yeni dizi) → loadGpsSessions yeni kimlik → reload yeni kimlik
   *   → useEffect([reload]) yeniden koşar → loadCockpit → …
   * GPS sekmesinde döngü görünmüyordu çünkü oradaki `if (days) return` freni tutuyor; Kokpit'in
   * böyle bir freni yok, her turda `days`i tazeliyor. Ref ile okumak davranışı değiştirmez
   * (aynı filtre için hâlâ tek kez çeker) ama `reload`u kararlı kılar.
   */
  const daysRef = useRef(days);
  daysRef.current = days;

  const loadGpsSessions = useCallback(async () => {
    if (daysRef.current) return; // aynı filtre için zaten yüklü
    setLoading(true);
    setError('');
    try {
      const dayList = await fetchVt('/days', {
        ...range, vendorId: aVendorId, workType: aWorkType, userIds: aRepresentativeId,
        groupByDay: true, take: 500,
      });
      setDays(dayList);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [range, aVendorId, aWorkType, aRepresentativeId]);

  const reload = useCallback(() => {
    // #786 "Listele"ye basılmadan HİÇBİR sorgu koşmaz — ekran boş durumda bekler.
    if (!applied) return undefined;
    if (tab === 'cockpit') return loadCockpit();
    if (tab === 'report') return loadReport();
    // #776 GPS sekmesi iz verisini kendi çeker (gps-track); alt katmanları çekmek boşa istek.
    // #804 ...ama hangi günü çizeceğini bilmek için gün listesi gerekir.
    if (tab === 'gps') return loadGpsSessions();
    return loadLowerLayers();
  }, [applied, tab, loadCockpit, loadLowerLayers, loadReport, loadGpsSessions]);

  useEffect(() => { reload(); }, [reload]);

  // ── Saha İzleme otomatik yenileme (§5.3) ───────────────────────────────────
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    if (tab !== 'watch' || !autoRefresh) return undefined;
    const id = setInterval(() => reloadRef.current(), refreshSec * 1000);
    return () => clearInterval(id);
  }, [tab, autoRefresh, refreshSec]);

  // ── Aksiyonlar ─────────────────────────────────────────────────────────────

  /** Kokpit satırı → alt katmana in. */
  function pickRepresentative(row) {
    setSelectedDay(row);
    setTab('visits');
  }

  async function openVisit(v) {
    setDrawerOpen(true);
    setVisitDetail(null);
    setVisitDetailLoading(true);
    try {
      setVisitDetail(await fetchVt(`/visits/${v.id}`));
    } catch (e) {
      setError(e.message);
      setDrawerOpen(false);
    } finally {
      setVisitDetailLoading(false);
    }
  }

  // #777.4/#777.5 Rota çözümü tek yerde (shared.activityTarget): belge hareketleri kadar
  // TAHSİLAT hareketleri de (documentType taşımayan finans işlemleri) gidilebilir.
  function openDocument(a) {
    const target = activityTarget(a);
    if (target) navigate(target);
  }

  async function deleteVisit(v) {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(
      `"${v.customerTitle || 'Ziyaret'}" kaydı silinecek. Silme nedeni (zorunlu):`,
    );
    if (!reason || !reason.trim()) return;
    try {
      await postVt(`/visits/${v.id}/delete`, { reason: reason.trim() });
      await loadLowerLayers();
    } catch (e) {
      setError(e.message);
    }
  }

  async function recalcDay() {
    if (!selectedDay) return;
    try {
      await postVt(`/days/${selectedDay.id}/recalculate`);
      await reload();
    } catch (e) {
      setError(e.message);
    }
  }

  async function reopenDay() {
    if (!selectedDay) return;
    // eslint-disable-next-line no-alert
    const reason = window.prompt('Gün yeniden açılacak. Neden (opsiyonel):') ?? '';
    try {
      await postVt(`/days/${selectedDay.id}/reopen`, { reason });
      await reload();
    } catch (e) {
      setError(e.message);
    }
  }

  async function closeDay() {
    if (!selectedDay) return;
    // eslint-disable-next-line no-alert
    const reason = window.prompt('Gün elle kapatılacak. Neden (opsiyonel):') ?? '';
    try {
      await postVt(`/days/${selectedDay.id}/close`, { reason });
      await reload();
    } catch (e) {
      setError(e.message);
    }
  }

  async function exportXlsx(kind) {
    const params = {
      ...range,
      vendorId,
      representativeId: selectedDay?.representativeId || representativeId,
      userIds: representativeId,
    };
    try {
      await downloadVt(`/${kind}`, params, `ziyaret-${kind}-${dateFrom}.xlsx`);
    } catch (e) {
      setError(e.message);
    }
  }

  // Bilinmeyen sayaç "0" olarak YAZILMAZ. Yeni sayaç kolonları geriye dönük doldurulmadı;
  // null'ı sıfıra çevirmek "o gün hiç ziyaret yapılmadı" demek olurdu — oysa yalnızca
  // henüz hesaplanmamıştır. Kullanıcı "Yeniden Hesapla" ile doldurabilir.
  const contextLabel = selectedDay
    ? [
      selectedDay.representativeName || 'Temsilci',
      selectedDay.vendorName || 'Distribütör tanımsız',
      selectedDay.visitCount == null
        ? 'ziyaret sayısı hesaplanmadı'
        : `${fmtNum(selectedDay.visitCount)} ziyaret`
        + (selectedDay.successVisitCount == null
          ? ' · başarı hesaplanmadı'
          : `, ${fmtNum(selectedDay.successVisitCount)} başarılı`),
    ].join(' · ')
    : null;

  return (
    <div className="sfa vt">
      <div className="pagehead">
        <div className="ph-txt">
          <div className="ttl">Ziyaret Takip</div>
          <div className="sub">Gün → ziyaret → hareket. Bir satıra tıklayınca bir alt katmana inersiniz.</div>
        </div>
        <div style={{ flex: 1 }} />
        {selectedDay && (
          <>
            <span className="pill nv" style={{ marginRight: 8 }}>
              {selectedDay.representativeName}
              <button type="button" onClick={() => setSelectedDay(null)}
                style={{
                  background: 'none', border: 'none', color: 'inherit',
                  cursor: 'pointer', marginLeft: 6, padding: 0, fontWeight: 700,
                }}
                title="Bağlamı temizle">×</button>
            </span>
            {canEdit && (
              <>
                <button type="button" className="btn sec sm" onClick={recalcDay} title="Türetilmiş toplamları yeniden üret">
                  <IcCalc size={15} />Yeniden Hesapla
                </button>
                {selectedDay.status === 'OPEN'
                  ? <button type="button" className="btn sec sm" onClick={closeDay}>Günü Kapat</button>
                  : <button type="button" className="btn sec sm" onClick={reopenDay}>
                    <IcUnlock size={15} />Günü Aç
                  </button>}
              </>
            )}
          </>
        )}
        <button type="button" className="btn sec sm" onClick={reload} disabled={loading}>
          <IcRefresh size={15} />Yenile
        </button>
      </div>

      {/* ── Sekmeler ──────────────────────────────────────────────────── */}
      <div className="seg" style={{ marginBottom: 14 }}>
        {TABS.map(({ key, label, Icon }) => (
          <button key={key} type="button" className={tab === key ? 'on' : ''} onClick={() => setTab(key)}>
            <Icon size={15} />{label}
          </button>
        ))}
      </div>

      {/* ── Filtre şeridi ─────────────────────────────────────────────── */}
      <div className="barcard" style={{ marginBottom: 14 }}>
        <div className="fgrid vt-filters">
          <Field label="Başlangıç Tarihi" required>
            <input className="inp" type="date" value={dateFrom} max={dateTo}
              onChange={(e) => setDateFrom(e.target.value)} />
          </Field>
          <Field label="Bitiş Tarihi" required>
            <input className="inp" type="date" value={dateTo} min={dateFrom}
              onChange={(e) => setDateTo(e.target.value)} />
          </Field>
          {/* #786 Distribütör ZORUNLU: seçilmeden listeleme yapılamaz. Bu alanlar (Distribütör /
              Çalışma Tipi / Temsilci) `wide` ile genişletildi — eskiden dar kalıyor, şeridin sağı
              boş duruyordu. */}
          <Field label="Distribütör" required wide>
            <select className="inp" value={vendorId}
              onChange={(e) => { setVendorId(e.target.value); setRepresentativeId(''); }}>
              <option value="">Seçiniz</option>
              {lookups.vendors.map((v) => (
                <option key={v.Id || v.id} value={v.Id || v.id}>{v.Name || v.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Çalışma Tipi" wide>
            <select className="inp" value={workType} onChange={(e) => setWorkType(e.target.value)}>
              <option value="">Tümü</option>
              {Object.entries(WORK_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </Field>
          {/* #786 Temsilci listesi SEÇİLİ DİSTRİBÜTÖRE göre daralır (eskiden tüm tanımlar geliyordu).
              Distribütör değişince seçim temizlenir — başka distribütörün temsilcisi asılı kalmasın. */}
          <Field label="Temsilci" wide>
            <select className="inp" value={representativeId} disabled={!vendorId}
              onChange={(e) => { setRepresentativeId(e.target.value); setSelectedDay(null); }}>
              <option value="">{vendorId ? `Tümü (${scopedRepresentatives.length})` : 'Önce distribütör seçin'}</option>
              {scopedRepresentatives.map((r) => (
                <option key={r.Id || r.id} value={r.Id || r.id}>{r.Name || r.name}</option>
              ))}
            </select>
          </Field>
        </div>

        {/* #786 LİSTELE — kriter şeridinin sağ alt köşesinde. Bu düğmeye basılmadan alt kısımda
            veri gösterilmez; her alan değişiminde ağır sorgu tetiklenmesi de böylece biter. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button
            type="button"
            className="btn pri"
            disabled={!vendorId || loading}
            title={!vendorId ? 'Listelemek için önce distribütör seçin' : undefined}
            onClick={() => {
              setSelectedDay(null);
              // #804 Gün listesi bayatlamasın: GPS sekmesinin tembel yükleyicisi "days doluysa
              // çekmez" der; yeni kriterde eski günleri çizmemesi için burada boşaltılır.
              setDays(null);
              setApplied({ dateFrom, dateTo, vendorId, workType, representativeId });
            }}
          >
            {loading ? 'Listeleniyor…' : 'Listele'}
          </button>
        </div>
      </div>

      {/* #786 Henüz listelenmediyse alt kısım BOŞ DURUM gösterir — yarım kriterle veri göstermek
          yerine kullanıcıya ne yapması gerektiği söylenir. */}
      {!applied && (
        <div className="barcard" style={{ padding: '28px 18px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Distribütör seçip <b>Listele</b> düğmesine basın.
        </div>
      )}

      {error && (
        <div className="alert info m" style={{ marginBottom: 12, borderLeftColor: 'var(--m-red)' }}>
          <div><b>Hata:</b> {error}</div>
        </div>
      )}

      {applied && tab === 'cockpit' && (
        <CockpitTab dashboard={dashboard} days={days} alerts={alerts} loading={loading}
          onPickRepresentative={pickRepresentative}
          onExcel={() => exportXlsx('summary')} />
      )}

      {applied && tab === 'visits' && (
        <VisitListTab data={visits} loading={loading} contextLabel={contextLabel}
          onOpenVisit={openVisit} onDeleteVisit={deleteVisit} canDelete={canDelete}
          onExcel={() => exportXlsx('visits')} />
      )}

      {/* #788 CANLI ŞERİT — "Sahada Şu An". `applied` KAPISINDAN MUAF: "kim güne başladı"
          sorusu bir tarih aralığı sorgusu değildir ve kullanıcı Listele'ye basmadan da bunu
          görebilmelidir (talebin şikâyeti tam olarak buydu). Yalnız Saha İzleme sekmesinde
          çizilir; sekmenin canlı ekseni zaten var (otomatik yenileme). */}
      {tab === 'watch' && (
        <LiveStrip vendorId={vendorId} refreshSec={refreshSec} autoRefresh={autoRefresh}
          onPick={(s) => setSelectedDay({
            id: s.sessionId,
            representativeId: s.representativeId,
            representativeName: s.representativeName,
            status: 'OPEN',
            visitCount: s.visitCount,
            successVisitCount: s.successVisitCount,
          })} />
      )}

      {applied && tab === 'watch' && (
        <FieldWatchTab activities={activities} dayDetail={dayDetail} loading={loading}
          contextLabel={contextLabel}
          autoRefresh={autoRefresh} onToggleAutoRefresh={() => setAutoRefresh((v) => !v)}
          refreshSec={refreshSec} onOpenDocument={openDocument} />
      )}

      {/* #776 GPS sekmesi kendi verisini seçili güne göre çeker (gps-track ucu oturum bazlıdır);
          bu yüzden loadLowerLayers'a bağlı değildir. */}
      {/* #804 `days` de geçilir: Kokpit'ten satır seçilmemişse GPS sekmesi filtrenin döndürdüğü
          gün oturumlarından en yenisini kendi seçip çizer (kullanıcı kriteri zaten vermiştir). */}
      {applied && tab === 'gps' && (
        <GpsTab selectedDay={selectedDay} contextLabel={contextLabel} days={days} />
      )}

      {applied && tab === 'report' && (
        <ReportTab report={report} loading={loading}
          filters={{
            dateFrom, dateTo,
            representativeLabel: selectedDay?.representativeName,
            vendorLabel: lookups.vendors.find((v) => (v.Id || v.id) === vendorId)?.Name,
          }}
          onExcel={() => exportXlsx('summary')} />
      )}

      <VisitDrawer open={drawerOpen} detail={visitDetail} loading={visitDetailLoading}
        onClose={() => setDrawerOpen(false)} onOpenDocument={openDocument} />
    </div>
  );
}

// #786 `wide`: Distribütör / Çalışma Tipi / Temsilci alanları tarih alanlarıyla aynı dar genişlikte
// duruyor ve şeridin sağı boş kalıyordu; bu üçü iki kolon kaplar (bkz. .fgrid .fld.wide).
function Field({ label, required, wide, children }) {
  return (
    <div className={wide ? 'fld wide' : 'fld'}>
      <label>{label}{required && <span className="req">*</span>}</label>
      {children}
    </div>
  );
}
